import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Chrome } from '@/components/layout/Chrome';
import { api, type ApiError } from '@/lib/api';
import { useApplicantStore } from './applicantStore';

const signupSchema = z
  .object({
    email: z.string().email('Please enter a valid email'),
    password: z.string().min(8, 'At least 8 characters'),
    confirm: z.string().min(8, 'Confirm your password'),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type SignupInput = z.infer<typeof signupSchema>;

export const SignupPage = () => {
  const navigate = useNavigate();
  const setPendingEmail = useApplicantStore((s) => s.setPendingEmail);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupInput>({ resolver: zodResolver(signupSchema) });

  const mutation = useMutation({
    mutationFn: async (input: SignupInput) => {
      await api.post('/applicants/register', {
        email: input.email,
        password: input.password,
      });
      return input.email;
    },
    onSuccess: (email) => {
      setPendingEmail(email);
      navigate('/apply/verify');
    },
  });

  const serverMessage =
    mutation.error instanceof AxiosError
      ? (mutation.error.response?.data as ApiError | undefined)?.error?.message
      : undefined;

  return (
    <Chrome>
      <div className="mx-auto max-w-md px-6 py-16">
        <h1 className="mb-2 text-2xl font-black text-navy">Create your account</h1>
        <p className="mb-6 text-sm text-gray-600">
          We'll email you a 6-digit code to verify your address before you start the registration.
        </p>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div>
            <label className="lbl">Email</label>
            <input
              {...register('email')}
              type="email"
              className={`inp ${errors.email ? 'inp-error' : ''}`}
              placeholder="you@example.com"
              autoComplete="email"
            />
            {errors.email && <p className="err-msg">{errors.email.message}</p>}
          </div>
          <div>
            <label className="lbl">Password</label>
            <input
              {...register('password')}
              type="password"
              className={`inp ${errors.password ? 'inp-error' : ''}`}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
            {errors.password && <p className="err-msg">{errors.password.message}</p>}
          </div>
          <div>
            <label className="lbl">Confirm Password</label>
            <input
              {...register('confirm')}
              type="password"
              className={`inp ${errors.confirm ? 'inp-error' : ''}`}
              placeholder="Repeat your password"
              autoComplete="new-password"
            />
            {errors.confirm && <p className="err-msg">{errors.confirm.message}</p>}
          </div>

          {serverMessage && (
            <p className="text-sm text-red-600">{serverMessage}</p>
          )}

          <button type="submit" disabled={mutation.isPending} className="btn-navy w-full">
            {mutation.isPending ? 'Sending code…' : 'Send verification code'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-2 block w-full text-center text-[11px] uppercase tracking-wider text-gray-500 hover:text-navy"
          >
            Back to login
          </button>
        </form>
      </div>
    </Chrome>
  );
};
